import { useCallback, useMemo, useSyncExternalStore } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/uz-latn';
import 'dayjs/locale/uz';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';

import uz from './locales/uz';
import uzCyrl from './locales/uz-Cyrl';
import ru from './locales/ru';
import en from './locales/en';

const STORAGE_KEY = 'wm-lang';
export const FALLBACK_LANGUAGE = 'uz';

export const LANGUAGES = [
  {
    code: 'uz',
    label: 'O‘zbekcha',
    short: 'UZ',
    intl: 'uz-Latn-UZ',
    dayjs: 'uz-latn',
    html: 'uz-Latn',
    compact: ['ming', 'mln', 'mlrd'],
  },
  {
    code: 'uz-Cyrl',
    label: 'Ўзбекча',
    short: 'ЎЗ',
    intl: 'uz-Cyrl-UZ',
    dayjs: 'uz',
    html: 'uz-Cyrl',
    compact: ['минг', 'млн', 'млрд'],
  },
  { code: 'ru', label: 'Русский', short: 'RU', intl: 'ru-RU', dayjs: 'ru', html: 'ru' },
  { code: 'en', label: 'English', short: 'EN', intl: 'en-US', dayjs: 'en', html: 'en' },
];

const dictionaries = { uz, 'uz-Cyrl': uzCyrl, ru, en };

const findLanguage = code => LANGUAGES.find(l => l.code === code);

const readLanguage = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return findLanguage(saved) ? saved : FALLBACK_LANGUAGE;
};

let currentLanguage = readLanguage();
const listeners = new Set();

const lookup = (dict, key) =>
  key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dict);

const interpolate = (text, params) =>
  params
    ? text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => (params[name] ?? '').toString())
    : text;

const warned = new Set();

export const translate = (language, key, params) => {
  let value = lookup(dictionaries[language], key);
  if (value == null) {
    value = lookup(dictionaries[FALLBACK_LANGUAGE], key);
    if (process.env.NODE_ENV !== 'production' && !warned.has(`${language}:${key}`)) {
      warned.add(`${language}:${key}`);
      console.warn(`[i18n] Missing "${key}" for "${language}"`);
    }
  }
  if (value == null) return key;
  return typeof value === 'string' ? interpolate(value, params) : value;
};

const applyLanguage = code => {
  const meta = findLanguage(code);
  dayjs.locale(meta.dayjs);
  document.documentElement.lang = meta.html;
};

applyLanguage(currentLanguage);

export const i18n = {
  get language() {
    return currentLanguage;
  },
  get meta() {
    return findLanguage(currentLanguage);
  },
  t: (key, params) => translate(currentLanguage, key, params),
  setLanguage(code) {
    if (!findLanguage(code) || code === currentLanguage) return;
    currentLanguage = code;
    localStorage.setItem(STORAGE_KEY, code);
    applyLanguage(code);
    listeners.forEach(listener => listener());
  },
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export const t = i18n.t;

const getSnapshot = () => currentLanguage;

export const useI18n = () => {
  const language = useSyncExternalStore(i18n.subscribe, getSnapshot);
  const tr = useCallback((key, params) => translate(language, key, params), [language]);
  return useMemo(
    () => ({
      t: tr,
      language,
      meta: findLanguage(language),
      setLanguage: i18n.setLanguage,
    }),
    [tr, language],
  );
};

export const findMissingKeys = () => {
  const collect = (node, prefix = '') =>
    Object.entries(node).flatMap(([k, v]) =>
      v && typeof v === 'object' && !Array.isArray(v) ? collect(v, `${prefix}${k}.`) : [`${prefix}${k}`],
    );
  const reference = collect(dictionaries[FALLBACK_LANGUAGE]);
  return Object.fromEntries(
    Object.entries(dictionaries).map(([code, dict]) => [
      code,
      reference.filter(key => lookup(dict, key) == null),
    ]),
  );
};
